/**
 * ParamsEditor — tables annuelles DAS (T4127 / TP-1015.F) modifiables par année + réinitialisation (Batch #14.250).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Save, RotateCcw, Plus, Trash2, Table2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH, Section, Btn, inputCls, Field, YEARS, num, err } from "./_shared";

const SECTIONS = [
  ["federal", "Impôt fédéral (T4127 — employé au Québec)"], ["quebec", "Impôt du Québec (TP-1015.F)"], ["qpp", "RRQ"], ["ei", "Assurance-emploi (taux Québec)"],
  ["qpip", "RQAP"], ["fss", "FSS (Fonds des services de santé)"], ["cnt", "CNT (normes du travail)"], ["cnesst", "CNESST"], ["wsdrf", "WSDRF (formation 1 %)"],
];
const LABELS = {
  lowest_rate: "Taux le plus bas", bpa_max: "MPB maximal", bpa_min: "MPB minimal", bpa_phase_start: "MPB — début réduction", bpa_phase_end: "MPB — fin réduction", cea: "Montant canadien pour emploi (CEA)",
  quebec_abatement: "Abattement du Québec", lcf_rate: "Crédit fonds de travailleurs (taux)", lcf_max: "Crédit fonds de travailleurs (max)", indexation: "Indexation",
  credit_rate: "Taux des crédits", bpa: "Montant personnel de base", employment_deduction_rate: "Déduction pour emploi (taux)", employment_deduction_max: "Déduction pour emploi (max)", fonds_credit_rate: "Crédit fonds (FTQ/Fondaction)",
  ympe: "MGA (YMPE)", yampe: "MSGA (YAMPE)", exemption: "Exemption de base", rate: "Taux", base_rate: "Taux de base", rate2: "Taux 2e cotisation", max_contrib: "Cotisation max.", max_base_contrib: "Cotisation de base max.", max_contrib2: "2e cotisation max.",
  mie: "Maximum de la rémunération assurable", rate_employee: "Taux salarié", employer_multiplier: "Multiplicateur employeur", max_employee: "Cotisation max. salarié", max_employer: "Cotisation max. employeur",
  max_insurable: "Maximum assurable", rate_employer: "Taux employeur", threshold_low: "Seuil masse salariale (bas)", threshold_high: "Seuil masse salariale (haut)", rate_low_services: "Taux ≤ seuil (services)", rate_low_primary: "Taux ≤ seuil (primaire/manufacturier)",
  rate_high: "Taux ≥ seuil haut", formula_services: "Formule services (a, b)", formula_primary: "Formule primaire (a, b)", max_earnings: "Maximum de gains", default_rate_per_100: "Taux par défaut ($/100 $)", threshold: "Seuil", min_wage: "Salaire minimum",
};

function Brackets({ rows, onChange, testId }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="sm:col-span-2 lg:col-span-4">
      <div className="text-[11px] font-semibold text-slate-600 mb-1">Tranches de revenu (jusqu'à · taux · constante K) — dernière tranche : « jusqu'à » vide</div>
      <div className="space-y-1" data-testid={testId}>
        {rows.map((b, i) => (
          <div key={i} className="flex gap-2 items-center">
            <input type="number" step="1" className={`${inputCls} w-36`} placeholder="∞" value={b.upto ?? ""} onChange={(e) => set(i, "upto", e.target.value)} />
            <input type="number" step="0.0001" className={`${inputCls} w-28`} value={b.rate} onChange={(e) => set(i, "rate", e.target.value)} />
            <input type="number" step="1" className={`${inputCls} w-28`} value={b.k} onChange={(e) => set(i, "k", e.target.value)} />
            <button onClick={() => onChange(rows.filter((_, j) => j !== i))} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <Btn tone="ghost" icon={Plus} onClick={() => onChange([...rows, { upto: "", rate: 0, k: 0 }])}>Ajouter une tranche</Btn>
      </div>
    </div>
  );
}

export default function ParamsEditor() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/payroll/params/${year}`, { headers: authH() }); setP(r.data); } catch (e) { err(e); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const setVal = (sec, k, v) => setP((x) => ({ ...x, [sec]: { ...x[sec], [k]: v } }));
  const save = async () => {
    setBusy("save");
    try {
      const body = JSON.parse(JSON.stringify(p));
      for (const [sec] of SECTIONS) {
        for (const [k, v] of Object.entries(body[sec])) {
          if (k === "brackets") body[sec][k] = v.map((b) => ({ upto: b.upto === "" || b.upto === null ? null : num(b.upto), rate: num(b.rate), k: num(b.k) }));
          else if (Array.isArray(v)) body[sec][k] = v.map(num);
          else body[sec][k] = num(v);
        }
      }
      body.min_wage = num(body.min_wage);
      const r = await axios.put(`${API}/admin/payroll/params/${year}`, body, { headers: authH() });
      setP(r.data); toast.success(`Paramètres ${year} enregistrés (source : personnalisée)`);
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const reset = async () => {
    if (!window.confirm(`Réinitialiser les paramètres ${year} aux valeurs officielles intégrées ?`)) return;
    setBusy("reset");
    try { const r = await axios.post(`${API}/admin/payroll/params/${year}/reset`, {}, { headers: authH() }); setP(r.data); toast.success("Paramètres réinitialisés"); } catch (e) { err(e); } finally { setBusy(""); }
  };

  return (
    <Section title="Tables annuelles DAS (constantes de calcul)" icon={Table2} testId="paie-params-editor"
      right={<div className="flex items-center gap-2">
        <select className={`${inputCls} w-28`} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="paie-params-year">{[...new Set([...YEARS, year + 1])].sort((a, b) => b - a).map((y) => <option key={y} value={y}>{y}</option>)}</select>
        <Btn tone="slate" icon={RotateCcw} onClick={reset} busy={busy === "reset"} testId="paie-params-reset">Valeurs officielles</Btn>
        <Btn tone="orange" icon={Save} onClick={save} busy={busy === "save"} testId="paie-params-save">Enregistrer</Btn>
      </div>}>
      {!p ? <p className="text-xs text-slate-500">Chargement…</p> : (
        <>
          <p className="text-xs text-slate-500" data-testid="paie-params-label">{p.label} — source : <b>{p.source === "custom" ? "personnalisée" : "valeurs officielles intégrées"}</b>{p.updated_at ? ` · mis à jour le ${String(p.updated_at).slice(0, 10)}` : ""}. Modifiez ces valeurs à chaque nouvelle année (publications T4127 / TP-1015.F) ou lorsqu'une constante change en cours d'année.</p>
          <Field label={LABELS.min_wage} className="sm:w-48"><input type="number" step="0.05" className={inputCls} value={p.min_wage ?? ""} onChange={(e) => setP((x) => ({ ...x, min_wage: e.target.value }))} /></Field>
          {SECTIONS.map(([sec, title]) => (
            <details key={sec} className="border border-slate-200 rounded-lg" open={sec === "federal" || sec === "quebec"} data-testid={`paie-params-${sec}`}>
              <summary className="px-3 py-2 text-xs font-bold text-slate-700 cursor-pointer bg-slate-50 rounded-lg">{title}</summary>
              <div className="p-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {Object.entries(p[sec] || {}).map(([k, v]) => {
                  if (k === "brackets") return <Brackets key={k} rows={v} onChange={(rows) => setVal(sec, k, rows)} testId={`paie-params-${sec}-brackets`} />;
                  if (Array.isArray(v)) return (
                    <Field key={k} label={LABELS[k] || k}><div className="flex gap-2">{v.map((x, i) => <input key={i} type="number" step="0.0001" className={inputCls} value={x} onChange={(e) => setVal(sec, k, v.map((y, j) => (j === i ? e.target.value : y)))} />)}</div></Field>
                  );
                  return <Field key={k} label={LABELS[k] || k}><input type="number" step="any" className={inputCls} value={v ?? ""} onChange={(e) => setVal(sec, k, e.target.value)} data-testid={`paie-params-${sec}-${k}`} /></Field>;
                })}
              </div>
            </details>
          ))}
        </>
      )}
    </Section>
  );
}
